import React, { useEffect, useState } from "react";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import BannerUploadModal from "../ChamberAdmin/BannerUploadModal";
import BannerPreviewModal from "../ChamberAdmin/BannerPreviewModal";
import DeleteBannerModal from "../ChamberAdmin/DeleteBannerModal";
import ActionDialog from "../../common/ActionDialog";
import PerksBannerPhoneMockup from "../../../images/perksbanner.jpeg";
import VideoIcon from "../../../images/lets-icons_video.png";
import { useEnumStrings } from "../../enumStrings";
import useGetChamberFromUserIdInAuth from "../../common/Hooks/useGetChamberFromUserIdInAuth";
import useGetChamberMemberFromUserIdInAuth from "../../common/Hooks/useGetChamberMemberFromUserIdInAuth";
import { CMP, useCmpPerkFormStyles } from "./cmpPerkFormStyles";

/**
 * CMP member design (CMP Accelerator.dc.html, Create Company Perks, featuredOn /
 * featuredOff blocks) for the perk banners. Same behaviour as
 * common/PerkBannerSection (upload, edit, delete, reorder, preview and the
 * "Purchase Featured Member" request), restyled for CMP member logins only.
 */
export const CMP_BANNER_COPY = {
    activated: "Featured Member Perk Banners Activated",
    notActivated: "Featured Member Perk Banners: NOT ACTIVATED",
    upgradedBefore: "Your company ",
    upgradedBold: "has upgraded to a Featured Member",
    upgradedAfter: ", and can upload up to 4 image or video banners to promote the perks on our Chamber Perks App™ and website directories",
    qualityBefore: "For best quality, we recommend an aspect ratio of ",
    qualityBold: "16:9 with a minimum size of 1280 × 720.",
    bannersAllow: "Featured Member Banners allow you to add image and video banners to your company profile and perks.",
    notUpgradedBefore: "You have ",
    notUpgradedBold: "not upgraded",
    notUpgradedMiddle: " to a featured member and therefore can not add image or video banners. If you would like to learn more, ",
    clickHere: "click here",
    notUpgradedAfter: " to contact your chamber.",
    footerBefore: "After previewing your banners, click ",
    footerAfter: " at bottom of page to upload them to our Chamber Perks App™ and website directories.",
    banner: "Banner",
    uploadBanner: "Upload Banner",
    previewBanners: "Preview Banners",
    watchVideo: "Watch Video",
};

const UploadIcon = ({ color, size }: { color: string; size: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 15.5V4.5" />
        <path d="M6.8 8.7l5.2-5.2 5.2 5.2" />
        <path d="M4 15.5v2.7a2.3 2.3 0 002.3 2.3h11.4a2.3 2.3 0 002.3-2.3v-2.7" />
    </svg>
);

interface CmpPerkBannerSectionProps {
    isFeaturedMember: boolean;
    formik: any;
    /** Bold action word in the banner footer ("Create Perk" in create mode, design "Update" in update mode). */
    submitLabel: string;
}

const CmpPerkBannerSection = ({ isFeaturedMember, formik, submitLabel }: CmpPerkBannerSectionProps): React.ReactElement => {
    const classes = useCmpPerkFormStyles();
    const enumStrings = useEnumStrings();
    const [bannerModalOpen, setBannerModalOpen] = useState(false);
    const [activeBannerIndex, setActiveBannerIndex] = useState(1);
    const [bannerPreviewOpen, setBannerPreviewOpen] = useState(false);
    const [deleteBannerPosition, setDeleteBannerPosition] = useState<number | null>(null);
    const [purchaseFeaturedOpen, setPurchaseFeaturedOpen] = useState(false);
    const chamberInfo = useGetChamberFromUserIdInAuth();
    const chamberMemberInfo = useGetChamberMemberFromUserIdInAuth();

    // Same position normalisation as common/PerkBannerSection.
    useEffect(() => {
        const banners = formik.values.banner || [];
        if (banners.length === 0) return;
        const sorted = [...banners]
            .filter((b: any) => b?.contentUrl)
            .sort((a: any, b: any) => (a?.position ?? 0) - (b?.position ?? 0));
        const needsReindex = sorted.some((b: any, i: number) => b.position !== i + 1);
        if (!needsReindex) return;
        formik.setFieldValue("banner", sorted.map((b: any, i: number) => ({ ...b, position: i + 1 })));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values.banner]);

    const swap = (position: number, target: number) => {
        const currentBanners = [...(formik.values.banner || [])];
        const thisIdx = currentBanners.findIndex((b: any) => b.position === position);
        const otherIdx = currentBanners.findIndex((b: any) => b.position === target);
        if (thisIdx >= 0 && otherIdx >= 0) {
            currentBanners[thisIdx] = { ...currentBanners[thisIdx], position: target };
            currentBanners[otherIdx] = { ...currentBanners[otherIdx], position };
            formik.setFieldValue("banner", currentBanners);
        }
    };

    const openUpload = (position: number) => {
        setActiveBannerIndex(position);
        setBannerModalOpen(true);
    };

    const highestPosition = Math.max(...(formik.values.banner || []).map((b: any) => b.position), 0);

    return (
        <div className={classes.bannerSection} data-testid="cmp-perk-banners">
            {isFeaturedMember ? (
                <>
                    <div className={classes.bannerPill} style={{ background: CMP.green500 }}>{CMP_BANNER_COPY.activated}</div>
                    <p className={classes.bannerCenterText}>
                        {CMP_BANNER_COPY.upgradedBefore}<b>{CMP_BANNER_COPY.upgradedBold}</b>{CMP_BANNER_COPY.upgradedAfter}
                    </p>
                    <p className={classes.bannerCenterText} style={{ lineHeight: "normal" }}>
                        {CMP_BANNER_COPY.qualityBefore}<b>{CMP_BANNER_COPY.qualityBold}</b>
                    </p>
                    <div className={classes.bannerGrid} style={{ maxWidth: "900px" }}>
                        {[1, 2, 3, 4].map((position) => {
                            const banner = formik.values.banner?.find((b: any) => b.position === position);
                            const leftDisabled = position === 1;
                            const rightDisabled = position === 4 || position >= highestPosition;
                            return (
                                <div key={position} className={classes.bannerTile}>
                                    <div className={classes.bannerTileLabel} style={{ color: CMP.gold600 }}>{CMP_BANNER_COPY.banner} {position}</div>
                                    {banner?.contentUrl ? (
                                        <div className={classes.bannerFilled}>
                                            <div className={classes.bannerMedia}>
                                                {banner.contentType === "IMAGE" ? (
                                                    <img src={banner.contentUrl} alt={`${CMP_BANNER_COPY.banner} ${position}`} className={classes.bannerImage} />
                                                ) : (
                                                    <div className={classes.bannerVideo}>
                                                        <img src={VideoIcon} alt="" style={{ width: "32px", height: "32px" }} />
                                                        {CMP_BANNER_COPY.watchVideo}
                                                    </div>
                                                )}
                                            </div>
                                            <div className={classes.bannerControls}>
                                                <div className={classes.bannerControlGroup}>
                                                    <button type="button" aria-label={`Edit ${CMP_BANNER_COPY.banner} ${position}`} className={classes.bannerEdit} onClick={() => openUpload(position)}>
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0A0A0A" strokeWidth="2" aria-hidden="true"><path d="M12 20h9M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4z" /></svg>
                                                    </button>
                                                    <button type="button" aria-label={`Delete ${CMP_BANNER_COPY.banner} ${position}`} className={classes.bannerDelete} onClick={() => setDeleteBannerPosition(position)}>
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" aria-hidden="true"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" /></svg>
                                                    </button>
                                                </div>
                                                <div className={classes.bannerControlGroup}>
                                                    <button
                                                        type="button"
                                                        aria-label={`Move ${CMP_BANNER_COPY.banner} ${position} left`}
                                                        className={classes.bannerArrow}
                                                        disabled={leftDisabled}
                                                        onClick={() => swap(position, position - 1)}
                                                        style={{ background: leftDisabled ? "#C6C6C6" : CMP.gold500, cursor: leftDisabled ? "default" : "pointer" }}
                                                    >❮</button>
                                                    <button
                                                        type="button"
                                                        aria-label={`Move ${CMP_BANNER_COPY.banner} ${position} right`}
                                                        className={classes.bannerArrow}
                                                        disabled={rightDisabled}
                                                        onClick={() => swap(position, position + 1)}
                                                        style={{ background: rightDisabled ? "#C6C6C6" : CMP.gold500, cursor: rightDisabled ? "default" : "pointer" }}
                                                    >❯</button>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <button type="button" className={classes.bannerUpload} onClick={() => openUpload(position)}>
                                            <UploadIcon color={CMP.gold500} size={24} />
                                            <span className={classes.bannerUploadText} style={{ color: CMP.gold500 }}>{CMP_BANNER_COPY.uploadBanner}</span>
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    <div className={classes.bannerFooter}>
                        {CMP_BANNER_COPY.footerBefore}<b>{submitLabel}</b>{CMP_BANNER_COPY.footerAfter}
                    </div>
                    <div className={classes.previewRow}>
                        <button type="button" className={classes.previewButton} style={{ background: CMP.black900, cursor: "pointer" }} onClick={() => setBannerPreviewOpen(true)}>
                            {CMP_BANNER_COPY.previewBanners}
                        </button>
                    </div>
                </>
            ) : (
                <>
                    <div className={classes.bannerPill} style={{ background: CMP.black900 }}>{CMP_BANNER_COPY.notActivated}</div>
                    <p className={classes.bannerCenterText}>{CMP_BANNER_COPY.bannersAllow}</p>
                    <p className={classes.bannerCenterText}>
                        {CMP_BANNER_COPY.notUpgradedBefore}<b>{CMP_BANNER_COPY.notUpgradedBold}</b>{CMP_BANNER_COPY.notUpgradedMiddle}
                        <button type="button" className={classes.clickHere} onClick={() => setPurchaseFeaturedOpen(true)}>{CMP_BANNER_COPY.clickHere}</button>
                        {CMP_BANNER_COPY.notUpgradedAfter}
                    </p>
                    <div className={classes.bannerGrid} style={{ maxWidth: "760px" }} aria-disabled="true">
                        {[1, 2, 3, 4].map((position) => (
                            <div key={position} className={classes.bannerTile}>
                                <div className={classes.bannerTileLabel} style={{ color: CMP.gray600 }}>{CMP_BANNER_COPY.banner} {position}</div>
                                <div className={classes.bannerLocked}>
                                    <UploadIcon color="#fff" size={22} />
                                    <span className={classes.bannerUploadText} style={{ color: CMP.white }}>{CMP_BANNER_COPY.uploadBanner}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className={classes.previewRow} style={{ marginTop: "12px" }}>
                        <button type="button" className={classes.previewButton} style={{ background: CMP.gray400, cursor: "default" }} disabled>
                            {CMP_BANNER_COPY.previewBanners}
                        </button>
                    </div>
                </>
            )}

            <BannerUploadModal
                open={bannerModalOpen}
                onClose={() => setBannerModalOpen(false)}
                bannerIndex={activeBannerIndex}
                useFeaturedMemberGuidance
                initialData={(() => {
                    const activeBanner = formik.values.banner?.find((b: any) => b.position === activeBannerIndex);
                    return {
                        image: activeBanner?.contentType === "IMAGE" ? activeBanner?.contentUrl : null,
                        videoUrl: activeBanner?.contentType === "VIDEO" ? activeBanner?.contentUrl : null,
                        redirectUrl: activeBanner?.imageLink || null,
                    };
                })()}
                onSave={(data) => {
                    const currentBanners = [...(formik.values.banner || [])];
                    if (data.image || data.videoUrl) {
                        const newBanner = {
                            position: activeBannerIndex,
                            contentUrl: data.image || data.videoUrl || "",
                            contentType: data.image ? "IMAGE" as any : "VIDEO" as any,
                            imageLink: data.redirectUrl || null,
                        };
                        const existingIndex = currentBanners.findIndex((b: any) => b.position === activeBannerIndex);
                        if (existingIndex >= 0) {
                            currentBanners[existingIndex] = newBanner;
                        } else {
                            currentBanners.push(newBanner);
                        }
                        currentBanners.sort((a: any, b: any) => a.position - b.position);
                        formik.setFieldValue("banner", currentBanners);
                    } else {
                        const repositioned = currentBanners
                            .filter((b: any) => b.position !== activeBannerIndex)
                            .map((b: any, i: number) => ({ ...b, position: i + 1 }));
                        formik.setFieldValue("banner", repositioned.length > 0 ? repositioned : undefined);
                    }
                    setBannerModalOpen(false);
                }}
            />

            <BannerPreviewModal
                open={bannerPreviewOpen}
                onClose={() => setBannerPreviewOpen(false)}
                banners={(formik.values.banner || []).filter((b: any) => b?.contentUrl).map((b: any) => ({
                    image: b?.contentType === "IMAGE" ? b?.contentUrl || null : null,
                    videoUrl: b?.contentType === "VIDEO" ? b?.contentUrl || null : null,
                    redirectUrl: b?.imageLink || null,
                }))}
                phoneMockupImage={PerksBannerPhoneMockup}
            />

            <DeleteBannerModal
                open={deleteBannerPosition !== null}
                onClose={() => setDeleteBannerPosition(null)}
                onDelete={() => {
                    if (deleteBannerPosition !== null) {
                        const repositioned = (formik.values.banner || [])
                            .filter((b: any) => b.position !== deleteBannerPosition)
                            .map((b: any, i: number) => ({ ...b, position: i + 1 }));
                        formik.setFieldValue("banner", repositioned.length > 0 ? repositioned : undefined);
                        setBannerPreviewOpen(false);
                        setDeleteBannerPosition(null);
                    }
                }}
            />
            <ActionDialog
                open={purchaseFeaturedOpen}
                onClose={() => setPurchaseFeaturedOpen(false)}
                icon={<MailOutlineIcon style={{ color: "#FFFFFF", fontSize: "40px" }} />}
                iconBgColor="#4CAF50"
                title="Purchase Featured Member"
                description="To upgrade to a Featured Member status and add image and video banners to your profile and perks. Send Request to Chamber."
                buttonText="Send Request"
                onButtonClick={() => {
                    const memberName = chamberMemberInfo?.name || "";
                    const subject = encodeURIComponent(enumStrings.createMemberForm.contactChamberEmailSubject.replace("{memberName}", memberName));
                    const body = encodeURIComponent(enumStrings.createMemberForm.contactChamberEmailBody
                        .replace("{chamberName}", chamberInfo?.name || "").replace("{memberName}", memberName));
                    window.open(`mailto:${chamberInfo?.supportContactEmail || ""}?subject=${subject}&body=${body}`, "_blank");
                    setPurchaseFeaturedOpen(false);
                }}
            />
        </div>
    );
};

export default CmpPerkBannerSection;
